import { useEffect } from "react";
import { motion } from "framer-motion";
import { useSettingsStore } from "../store/settings";
import type { Settings } from "@shared/types";

const LANGS: { value: Settings["language"]; label: string }[] = [
  { value: "en", label: "English" },
  { value: "tr", label: "Türkçe" },
  { value: "de", label: "Deutsch" },
];
const TARGETS: { value: Settings["targetType"]; label: string; hint: string }[] = [
  { value: "word", label: "Word", hint: "Guess a single secret word." },
  { value: "sentence", label: "Sentence", hint: "LLM writes a sentence containing a seed word — guess the sentence." },
];
const MODES: { value: Settings["mode"]; label: string; hint: string }[] = [
  { value: "classic", label: "Classic", hint: "Unlimited guesses. Take your time." },
  { value: "timeattack", label: "Time-Attack", hint: "Race the clock to get closest." },
];
const DIFFICULTIES: { value: Settings["difficulty"]; label: string }[] = [
  { value: "easy", label: "easy" },
  { value: "normal", label: "normal" },
  { value: "hard", label: "hard" },
];

export function SettingsPage() {
  const settings = useSettingsStore((s) => s.settings);
  const load = useSettingsStore((s) => s.load);
  const save = useSettingsStore((s) => s.save);
  const loaded = useSettingsStore((s) => s.loaded);
  const saving = useSettingsStore((s) => s.saving);

  useEffect(() => {
    if (!loaded) load();
  }, [loaded, load]);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto flex flex-col gap-6">
      <div>
        <h1 className="font-display text-3xl">settings</h1>
        <p className="text-ink-400 text-sm mt-1">saved automatically · {saving ? "saving…" : "synced"}</p>
      </div>

      <Section title="language">
        <div className="flex gap-2 flex-wrap">
          {LANGS.map((l) => (
            <Pill
              key={l.value}
              active={settings.language === l.value}
              onClick={() => save({ language: l.value })}
            >
              {l.label}
            </Pill>
          ))}
        </div>
      </Section>

      <Section title="target">
        <div className="flex flex-col gap-2">
          {TARGETS.map((t) => (
            <OptionCard
              key={t.value}
              active={settings.targetType === t.value}
              label={t.label}
              hint={t.hint}
              onClick={() => save({ targetType: t.value })}
            />
          ))}
        </div>
      </Section>

      <Section title="mode">
        <div className="flex flex-col gap-2">
          {MODES.map((m) => (
            <OptionCard
              key={m.value}
              active={settings.mode === m.value}
              label={m.label}
              hint={m.hint}
              onClick={() => save({ mode: m.value })}
            />
          ))}
        </div>
        {settings.mode === "timeattack" && (
          <div className="mt-4">
            <label className="text-sm text-ink-300 flex items-center gap-3">
              <span className="w-24">time limit</span>
              <input
                type="range"
                min={15}
                max={180}
                step={5}
                value={settings.timeLimitSeconds}
                onChange={(e) => save({ timeLimitSeconds: Number(e.target.value) })}
                className="flex-1 accent-accent"
              />
              <span className="font-mono w-12 text-right">{settings.timeLimitSeconds}s</span>
            </label>
          </div>
        )}
      </Section>

      <Section title="difficulty">
        <div className="flex gap-2 flex-wrap">
          {DIFFICULTIES.map((d) => (
            <Pill
              key={d.value}
              active={settings.difficulty === d.value}
              onClick={() => save({ difficulty: d.value })}
            >
              {d.label}
            </Pill>
          ))}
        </div>
        <p className="text-xs text-ink-400 mt-2">
          difficulty changes the style of generated sentences (vocabulary / abstractness).
        </p>
      </Section>
    </motion.div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="glass rounded-2xl p-5">
      <h2 className="text-xs uppercase tracking-[0.2em] text-ink-400 mb-3">{title}</h2>
      {children}
    </section>
  );
}

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-1.5 rounded-full text-sm transition ${
        active
          ? "bg-accent text-ink-950 font-medium"
          : "glass-soft text-ink-200 hover:text-ink-100"
      }`}
    >
      {children}
    </button>
  );
}

function OptionCard({
  active,
  label,
  hint,
  onClick,
}: {
  active: boolean;
  label: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`text-left p-3 rounded-xl border transition ${
        active
          ? "border-accent/70 bg-accent/10"
          : "border-white/5 hover:border-white/15 bg-white/[0.02]"
      }`}
    >
      <div className="flex items-center gap-2">
        <span
          className={`w-2.5 h-2.5 rounded-full ${active ? "bg-accent shadow-glow" : "bg-ink-500"}`}
        />
        <span className="font-medium">{label}</span>
      </div>
      <div className="text-xs text-ink-400 mt-1 ml-5">{hint}</div>
    </button>
  );
}
